import { ReactNode } from 'react'

import { clsx } from 'clsx'
import { Link } from 'react-router-dom'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { Space } from '@tribeplatform/gql-client/types'
import { SpacePicker } from '@tribeplatform/react-components/EntityPicker'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useAuthMember,
  useModerationCount,
} from '@tribeplatform/react-sdk/hooks'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { IndicatorBadge } from '@tribeplatform/react-ui-kit/IndicatorBadge'
import { NavVertical } from '@tribeplatform/react-ui-kit/Sidebar'

import { ModerationTab } from './types.js'

interface ModerationSidebarProps {
  selectedTab: ModerationTab
  onTabChange: (selectedTab: ModerationTab) => void
  maybeSpace: null | Space
  onSpaceChange: (maybeSpace: null | Space) => void
}

export const ModerationSidebar = (props: ModerationSidebarProps) => {
  const { onSpaceChange, maybeSpace, onTabChange, selectedTab } = props
  const { $t } = useI18n()
  const { isModerator } = useAuthMember()

  const {
    pendingMembersCount,
    pendingPostsCount,
    reportedMembersCount,
    reportedPostsCount,
    draftPostsCount,
    scheduledPostsCount,
  } = useModerationCount(maybeSpace?.id)

  const handleTabClick = (tab: ModerationTab) => () => {
    onTabChange(tab)
  }

  return (
    <NavVertical className="space-y-5">
      <NavVertical.Group>
        <NavVertical.Item
          as={Link}
          leadingIcon={
            <SvgIcon className="rtl:-scale-x-100" name="arrow-left" />
          }
          to="/"
          className="mb-3"
        >
          <T
            defaultMessage="Back to {COMMUNITY}"
            id="Generics.BackToCommunity"
          />
        </NavVertical.Item>
        <SpacePicker
          value={maybeSpace}
          onChange={onSpaceChange}
          fields={{
            authMemberProps: 'all',
          }}
          variables={{
            limit: 1000,
          }}
          customFilter={space => {
            const [canApproveSpaceMembershipRequest] = hasScopesPermission(
              space,
              ['approveSpaceMembershipRequest'],
            )
            return canApproveSpaceMembershipRequest
          }}
          placeholder={$t({
            id: 'Moderation.Sidebar.SpacePicker.Placeholder',
            defaultMessage: 'Pick a {SPACE}...',
          })}
        />
      </NavVertical.Group>
      <NavVertical.Group>
        <SectionTitle>
          <T
            defaultMessage="Content planning"
            id="Moderation.Sidebar.ContentPlanning"
          />
        </SectionTitle>
        <ModerationTabItem
          selectedTab={selectedTab}
          tabValue="draft-posts"
          onClick={handleTabClick}
          text={$t({
            defaultMessage: 'Draft posts',
            id: 'Moderation.Tab.Posts.Draft',
          })}
          count={draftPostsCount}
          icon={<SvgIcon name="notebook-pen" />}
        />
        <ModerationTabItem
          selectedTab={selectedTab}
          tabValue="scheduled-posts"
          onClick={handleTabClick}
          text={$t({
            defaultMessage: 'Scheduled posts',
            id: 'Moderation.Tab.Posts.Scheduled',
          })}
          count={scheduledPostsCount}
          icon={<SvgIcon name="calendar" />}
        />
      </NavVertical.Group>
      <NavVertical.Group>
        <SectionTitle>
          <T
            defaultMessage="Content moderation"
            id="Moderation.Sidebar.ContentModeration"
          />
        </SectionTitle>
        <ModerationTabItem
          selectedTab={selectedTab}
          tabValue="pending-posts"
          onClick={handleTabClick}
          text={$t({
            defaultMessage: 'Pending posts',
            id: 'Moderation.Tab.Posts.Pending',
          })}
          count={pendingPostsCount}
          icon={<SvgIcon name="moderation-pending-post" />}
        />

        <ModerationTabItem
          selectedTab={selectedTab}
          tabValue="reported-posts"
          onClick={handleTabClick}
          text={$t({
            defaultMessage: 'Reported posts',
            id: 'Moderation.Tab.Posts.Reported',
          })}
          count={reportedPostsCount}
          icon={<SvgIcon name="moderation-reported-post" />}
        />
      </NavVertical.Group>
      <NavVertical.Group>
        <SectionTitle>
          <T
            defaultMessage="Member moderation"
            id="Moderation.Sidebar.MemberModeration"
          />
        </SectionTitle>
        <ModerationTabItem
          selectedTab={selectedTab}
          tabValue="member-requests"
          onClick={handleTabClick}
          text={$t({
            defaultMessage: 'Pending {MEMBERS}',
            id: 'Moderation.Tab.Members.Pending',
          })}
          count={pendingMembersCount}
          icon={<SvgIcon name="moderation-pending-user" />}
        />

        {isModerator && (
          <ModerationTabItem
            selectedTab={selectedTab}
            tabValue="reported-members"
            onClick={handleTabClick}
            text={$t({
              defaultMessage: 'Reported {MEMBERS}',
              id: 'Moderation.Tab.Members.Reported',
            })}
            count={reportedMembersCount}
            icon={<SvgIcon name="moderation-reported-user" />}
          />
        )}
      </NavVertical.Group>
    </NavVertical>
  )
}

interface ModerationTabItemProps {
  onClick: (tab: ModerationTab) => () => void
  tabValue: ModerationTab
  selectedTab: ModerationTab
  text: string
  count: number
  icon: ReactNode
}

const ModerationTabItem = (props: ModerationTabItemProps) => {
  const { onClick, tabValue, selectedTab, text, count, icon } = props

  return (
    <NavVertical.Item
      leadingIcon={icon}
      className={tabItemClassName(selectedTab === tabValue)}
      onClick={onClick(tabValue)}
      trailingAddon={
        <IndicatorBadge variant="neutral" count={count} showZero />
      }
    >
      {text}
    </NavVertical.Item>
  )
}

const tabItemClassName = (selected: boolean) =>
  clsx(
    selected
      ? 'bg-background-hovered text-content-on-background-hovered'
      : 'text-content-subdued',
    'group relative text-start font-normal cursor-pointer',
    'py-2 px-5 m-0 text-sm',
    'focus:outline-none focus-visible:ring focus-visible:ring-inset focus:z-10',
  )

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <div className="text-content-muted text-sm font-normal px-5">{children}</div>
)
